import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Award as AwardIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SortableList from "@/components/admin/SortableList";
import MediaDropzone from "@/components/admin/MediaDropzone";

const EMPTY = { title: "", image_url: "", public_id: "", year: "", link: "", order: 0 };

export default function AdminAwards() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    return api.get("/awards").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (it) => {
    try {
      if (it.id) {
        const { data } = await api.patch(`/awards/${it.id}`, it);
        setItems((all) => all.map((x) => (x.id === data.id ? data : x)).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
        toast.success("Award updated");
      } else {
        const { data } = await api.post("/awards", { ...it, order: items.length + 1 });
        setItems((all) => [...all, data]);
        toast.success("Award added");
      }
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this award?")) return;
    try {
      await api.delete(`/awards/${id}`);
      setItems((all) => all.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const reorder = async (ids, next) => {
    setItems(next);  // optimistic
    try { await api.post("/awards/reorder", { ids }); }
    catch (e) { toast.error("Reorder failed"); load(); }
  };

  return (
    <div data-testid="admin-awards-page">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Recognition</div>
          <h1 className="font-display text-3xl md:text-4xl text-charcoal">Awards</h1>
          <p className="text-charcoal/65 font-light mt-2">Drag to reorder. Upload logos directly.</p>
        </div>
        <button type="button" onClick={() => setEditing({ ...EMPTY })} data-testid="awards-add-btn" className="btn-primary h-11 px-5 text-[11px]">
          <Plus className="h-4 w-4" /> Add award
        </button>
      </header>

      {loading ? (
        <div className="text-charcoal/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-charcoal/40 italic">No awards yet.</div>
      ) : (
        <SortableList
          items={items}
          onReorder={reorder}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
          renderItem={(it, Handle) => (
            <div data-testid={`award-card-${it.id}`} className="bg-white border border-beige relative group">
              <div className="absolute top-2 left-2 z-10">{Handle}</div>
              <div className="aspect-[3/2] flex items-center justify-center bg-sage/20 overflow-hidden">
                {it.image_url ? (
                  <img src={it.image_url} alt={it.title} className="max-h-full max-w-full object-contain p-6" />
                ) : (
                  <AwardIcon className="h-12 w-12 text-gold" strokeWidth={1.2} />
                )}
              </div>
              <div className="p-4">
                <div className="font-display text-lg text-charcoal truncate">{it.title}</div>
                <div className="text-xs text-charcoal/55">{it.year || "—"}</div>
              </div>
              <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" onClick={() => setEditing(it)} className="h-8 w-8 bg-ivory border border-beige inline-flex items-center justify-center text-charcoal hover:bg-teal-600 hover:text-ivory">
                  <Save className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => remove(it.id)} className="h-8 w-8 bg-ivory border border-beige inline-flex items-center justify-center text-rose-700 hover:bg-rose-700 hover:text-ivory">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        />
      )}

      {editing ? <EditModal item={editing} onClose={() => setEditing(null)} onSave={save} /> : null}
    </div>
  );
}

function EditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({ ...item });
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="award-edit-modal">
      <div className="absolute inset-0 bg-charcoal/45" onClick={onClose} />
      <div className="relative bg-ivory border border-beige w-full max-w-lg p-7 max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 h-9 w-9 border border-beige hover:bg-charcoal hover:text-ivory inline-flex items-center justify-center">
          <X className="h-4 w-4" />
        </button>
        <div className="font-display text-2xl text-charcoal mb-5">{form.id ? "Edit award" : "Add award"}</div>

        <div className="mb-5">
          <MediaDropzone
            folder="mostash/awards"
            accept="image"
            height="aspect-[3/2]"
            label="Award logo / badge"
            testid="award-image-dropzone"
            value={form.image_url ? { url: form.image_url, public_id: form.public_id, resource_type: "image" } : null}
            onChange={(asset) => setForm((f) => ({ ...f, image_url: asset?.url || "", public_id: asset?.public_id || "" }))}
          />
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Title *</span>
            <input data-testid="award-title" name="title" value={form.title} onChange={onChange} className="mt-2 field-input" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Year</span>
              <input name="year" value={form.year || ""} onChange={onChange} className="mt-2 field-input" placeholder="e.g. 2024" />
            </label>
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Link (optional)</span>
              <input name="link" value={form.link || ""} onChange={onChange} className="mt-2 field-input" placeholder="https://…" />
            </label>
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary h-10 px-5 text-[11px]">Cancel</button>
          <button type="button" onClick={() => onSave(form)} data-testid="award-edit-save" className="btn-primary h-10 px-5 text-[11px]">Save</button>
        </div>
      </div>
    </div>
  );
}
